'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import OtpAuthModal, { OtpAuthContextState, OtpAuthSource } from '../components/auth/OtpAuthModal'

export interface OtpAuthOpenOptions extends Partial<OtpAuthContextState> {
  /**
   * Run when the visitor closes the modal — X or backdrop — *without* logging
   * in. A successful login never fires it.
   *
   * A page that gates itself behind the modal (`/orders/new`) needs this to
   * take the visitor somewhere they can still use; otherwise "بستن" leaves
   * them on a page that shows nothing but "ابتدا وارد شوید".
   */
  onDismiss?: () => void
}

interface OtpAuthContextValue {
  open: (options?: OtpAuthOpenOptions) => void
  close: () => void
  isOpen: boolean
}

const OtpAuthContext = createContext<OtpAuthContextValue | undefined>(undefined)

export function OtpAuthProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [contextState, setContextState] = useState<OtpAuthContextState>({
    source: 'generic',
  })
  const dismissRef = useRef<(() => void) | null>(null)

  // `open` and `close` keep a stable identity across renders. They used to be
  // rebuilt whenever `isOpen` flipped, which made every `useEffect` that lists
  // `open` in its deps re-run on close — and a page that opens the modal for
  // guests re-opened it instantly, so the close button looked dead.
  const open = useCallback((options?: OtpAuthOpenOptions) => {
    dismissRef.current = options?.onDismiss ?? null
    setContextState({
      source: (options?.source as OtpAuthSource) || 'generic',
      redirectTo: options?.redirectTo,
      role: options?.role,
    })
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    const dismiss = dismissRef.current
    dismissRef.current = null
    dismiss?.()
  }, [])

  /** Closed because the login succeeded — the caller got what it wanted. */
  const finish = useCallback(() => {
    dismissRef.current = null
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      open,
      close,
      isOpen,
    }),
    [open, close, isOpen],
  )

  return (
    <OtpAuthContext.Provider value={value}>
      {children}
      <OtpAuthModal
        isOpen={isOpen}
        onClose={close}
        onAuthenticated={finish}
        context={contextState}
      />
    </OtpAuthContext.Provider>
  )
}

export function useOtpAuth() {
  const context = useContext(OtpAuthContext)
  if (!context) {
    throw new Error('useOtpAuth must be used within an OtpAuthProvider')
  }
  return context
}
